import type { PrintElement, ToHtmlOptions } from "../core/types";
import { toCssLength } from "../core/units";
import { renderBarcodeSvg, renderQrCodeSvg } from "../elements/barcode";
import { renderTableElement } from "../elements/table";
import { escapeHtml, inlineStyleObjectToString } from "../utils/html";

function buildElementClassNames(element: PrintElement): string {
  return [
    "printer-element",
    element.className,
    element.keepTogether ? "printer-element--keep-together" : "",
    element.pageBreakBefore ? "printer-element--page-break-before" : "",
    element.pageBreakAfter ? "printer-element--page-break-after" : ""
  ]
    .filter(Boolean)
    .join(" ");
}

function buildLayoutStyle(element: PrintElement): string {
  const style: Record<string, string | number | undefined> = {
    ...element.style
  };

  if (element.box?.x !== undefined || element.box?.y !== undefined) {
    style.position = "absolute";
  }

  if (element.box?.x !== undefined) {
    style.left = toCssLength(element.box.x);
  }

  if (element.box?.y !== undefined) {
    style.top = toCssLength(element.box.y);
  }

  if (element.box?.width !== undefined) {
    style.width = toCssLength(element.box.width);
  }

  if (element.box?.height !== undefined) {
    style.height = toCssLength(element.box.height);
  }

  return inlineStyleObjectToString(style);
}

function wrapElement(element: PrintElement, innerHtml: string, index?: number): string {
  const id = element.id ? ` id="${escapeHtml(element.id)}"` : "";
  const style = buildLayoutStyle(element);
  const styleAttr = style ? ` style="${style}"` : "";
  const dataIndex = index !== undefined ? ` data-printer-index="${index}"` : "";
  return `<section${id}${dataIndex} class="${buildElementClassNames(element)}"${styleAttr}>${innerHtml}</section>`;
}

function isPositionedElement(element: PrintElement): boolean {
  return element.box?.x !== undefined || element.box?.y !== undefined;
}

// Render a single semantic print element. Manual page-break markers are only
// emitted in print mode because preview mode can segment pages structurally.
export async function renderElement(element: PrintElement, options: ToHtmlOptions = {}, index?: number): Promise<string> {
  switch (element.kind) {
    case "text": {
      const tagName = element.tagName ?? "p";
      return wrapElement(element, `<${tagName}>${escapeHtml(element.text)}</${tagName}>`, index);
    }
    case "html":
      return wrapElement(element, element.html, index);
    case "image": {
      const fit = element.fit ?? "contain";
      const alt = escapeHtml(element.alt ?? "");
      return wrapElement(
        element,
        `<img class="printer-image" src="${escapeHtml(element.src)}" alt="${alt}" style="object-fit:${fit};width:100%;height:100%;" />`,
        index
      );
    }
    case "pageBreak":
      return options.mode === "print" ? '<div class="printer-page-break" aria-hidden="true"></div>' : "";
    case "table":
      return wrapElement(element, renderTableElement(element), index);
    case "barcode":
      return wrapElement(element, `<div class="printer-code">${renderBarcodeSvg(element)}</div>`, index);
    case "qrcode":
      return wrapElement(element, `<div class="printer-qrcode">${await renderQrCodeSvg(element)}</div>`, index);
    default:
      return "";
  }
}

export async function renderElements(elements: PrintElement[], options: ToHtmlOptions = {}): Promise<string> {
  const rendered = await Promise.all(elements.map((element, index) => renderElement(element, options, index)));
  return rendered.join("");
}

// Wrap a rendered element stream into a single logical page container. Preview
// keeps flowing content and positioned template overlays in separate layers so
// spacing rules never shift absolute coordinates.
export async function renderPageWrapper(
  elements: PrintElement[],
  options: ToHtmlOptions = {},
  pageOptions: { className?: string; pageNumber?: number; showPageNumbers?: boolean; forcePageBreakAfter?: boolean } = {}
): Promise<string> {
  const flowElements = elements.filter((element) => !isPositionedElement(element));
  const overlayElements = elements.filter((element) => isPositionedElement(element));
  const pageNumber = pageOptions.showPageNumbers && pageOptions.pageNumber
    ? `<div class="printer-page-number">Page ${pageOptions.pageNumber}</div>`
    : "";
  const flowHtml = await renderElements(flowElements, options);
  const overlayHtml = await renderElements(overlayElements, options);
  const forceBreakClass = pageOptions.forcePageBreakAfter ? " printer-document--manual-break-after" : "";

  return `<main class="printer-document${forceBreakClass}${pageOptions.className ? ` ${pageOptions.className}` : ""}">${pageNumber}<div class="printer-page-content"><div class="printer-flow">${flowHtml}</div><div class="printer-overlay">${overlayHtml}</div></div></main>`;
}
